// this is a new version of the tabs component
import React, { Suspense, useEffect, useState } from 'react';
import { LayoutPadding } from '~/layouts/index';
import { getMobileImagePlacement, getSettingsClass } from '../../utils/helpers';
import RiveImage from '../fragments/riveImage';
import { graphql } from 'gatsby';
import { useViewport } from '~/utils/viewport-provider';
import { VIEWPORT } from '~/utils/constants';

interface TabsProps {
  settings: Settings;
  btab_list: BTabItem[];
  tab_position: string;
  show_bunnies_image: boolean;
}

const preloadImage = (src: string) => {
  new Image().src = src;
};

// TinaCMS has a limitation that doesn't allow us to have a single templated field.
// [THE_ONLY_ELEMENT] means that we are only interested in the first element of the array.
// This is a necessary workaround to make the component work with TinaCMS.
const THE_ONLY_ELEMENT = 0;
const BTabs: React.FC<TabsProps> = ({
  settings,
  btab_list,
  tab_position,
  show_bunnies_image,
}) => {
  const { viewport } = useViewport();
  const [activeTab, setActiveTab] = useState(btab_list[0]?.title);
  const bunnyImageClass = show_bunnies_image ? ' show-image' : '';
  const positionClass = tab_position;
  const settingsClass = getSettingsClass(settings);

  const changeTab = (title: string) => (): void => {
    if (title !== activeTab) {
      setActiveTab(title);
    }
  };

  useEffect(() => {
    // preload all images
    btab_list.forEach(({ image: legacyImageSrc, image_field }) => {
      const imageTemplate =
        image_field && image_field.length > 0
          ? image_field[THE_ONLY_ELEMENT]
          : null;

      let preloadSrc = legacyImageSrc;
      if (imageTemplate?._template === 'reusable_image') {
        preloadSrc = imageTemplate.src;
      }
      if (preloadSrc) {
        preloadImage(preloadSrc);
      }
    });
  }, []);

  return (
    <LayoutPadding>
      <div
        className={`block__btabs block ${settingsClass} ${positionClass} ${bunnyImageClass}`}
      >
        <ul className='btabs-header'>
          {btab_list.map(tab => (
            <li
              key={tab.title}
              className={`btab ${activeTab === tab.title ? 'active' : ''}`}
              onClick={changeTab(tab.title)}
            >
              <div className='btab__animate' />
              <div className='btab__title'>{tab.tab_title}</div>
              <div
                className='btab__description'
                dangerouslySetInnerHTML={{ __html: tab.text }}
              />
              {viewport.device === VIEWPORT.mobile ? (
                <BTabImage
                  key={tab.title}
                  tab={tab}
                  isVisibleOnMobile={true}
                  activeTab={activeTab === tab.title}
                />
              ) : null}
            </li>
          ))}
        </ul>
        {viewport.device !== VIEWPORT.mobile ? (
          <div className='btabs-media'>
            {btab_list.map(tab => (
              <BTabImage
                key={tab.title}
                tab={tab}
                isVisibleOnMobile={false}
                activeTab={activeTab === tab.title}
              />
            ))}
          </div>
        ) : null}
      </div>
    </LayoutPadding>
  );
};

const BTabImage: React.FC<{
  tab: BTabItem;
  isVisibleOnMobile?: boolean;
  activeTab?: boolean;
  mobile_image_placement?: MobileImagePlacement;
}> = React.memo(({ tab, isVisibleOnMobile = false, activeTab = false }) => {
  const { isDesktop, isTablet } = useViewport();
  let Component = null;
  let imageStyleOverride: {} | { style: React.CSSProperties } = {};
  if (tab.image) {
    // legacy field
    Component = (
      <ImageField
        data={{
          _template: 'reusable_image',
          src: tab.image,
          alt: tab.image_alt_text,
        }}
        isVisibleOnMobile
      />
    );
  } else if (tab.image_field && tab.image_field.length > 0) {
    Component = (
      <ImageField
        data={tab.image_field[THE_ONLY_ELEMENT]}
        isVisibleOnMobile={isVisibleOnMobile}
      />
    );

    imageStyleOverride = {
      style: {
        '--translate-x': `${tab.image_field[0]?.offset?.horizontal ?? 0}rem`,
        '--translate-y': `${tab.image_field[0]?.offset?.vertical ?? 0}rem`,
        '--scale-x': tab.image_field[0]?.scale?.x ?? 1,
        '--scale-y': tab.image_field[0]?.scale?.y ?? 1,
        '--image-width':
          (
            (isDesktop
              ? tab.image_field[0]?.responsive_width?.desktop
              : isTablet
                ? tab.image_field[0]?.responsive_width?.mobile
                : '') || '100'
          ).toString() + (tab.image_field[0]?.responsive_width?.unit || '%'),
      } as React.CSSProperties,
    };
  }

  return (
    <div
      className={activeTab ? 'block' : 'hidden'}
      {...imageStyleOverride}
    >
      {Component}
    </div>
  );
});

const ImageField: React.FC<{
  data?: ImageField<ReusableImageField | ReusableRiveField>;
  isVisibleOnMobile?: boolean;
  className?: string;
}> = ({ data, isVisibleOnMobile = false }) => {
  if (!data) return null;
  const { isDesktop, isTablet } = useViewport();
  const mobileImagePlacement = getMobileImagePlacement(
    data.mobile_image_placement
  );

  if (data._template === 'reusable_image') {
    return (
      <img
        className='btabs-media__image image--native'
        src={data.src}
        alt={data.alt}
        style={mobileImagePlacement}
      />
    );
  } else if (data._template === 'reusable_rive') {
    return (
      <div
        className='rive-wrapper relative mx-auto my-auto h-[500px] w-[500px] pb-8'
        style={{
          width:
            (
              (isDesktop
                ? data.responsive_width?.desktop
                : isTablet
                  ? data.responsive_width?.mobile
                  : '') ||
              '' ||
              ''
            ).toString() + (data.responsive_width?.unit || '') || '',
          height:
            (
              (isDesktop
                ? data.responsive_width?.desktop
                : isTablet
                  ? data.responsive_width?.mobile
                  : '') || ''
            ).toString() + (data.responsive_width?.unit || '') || '',
        }}
      >
        <Suspense fallback={<div className='hero-cover-image'></div>}>
          <RiveImage
            classNames='btabs-media__image image--rive'
            fit='contain'
            position='center'
            block={{
              rive_state_machine_name: data.rive_state_machine_name,
              rive_autoplay: !!data.rive_autoplay,
              rive_image: data.rive_image,
            }}
            shouldDisableRiveListeners={isVisibleOnMobile}
          />
        </Suspense>
      </div>
    );
  }

  return null;
};

export default BTabs;

export const bTabItemFragment = graphql`
  fragment BTabItemFragment on BTabItem {
    _template
    tab_icon
    tab_title
    text
    title
    image
    image_alt_text
    cta_field {
      ...CtaFieldFragment
    }
    html
    image_field {
      ...ImageFieldFragment
    }
  }
`;

export const bTabsFragmentQuery = graphql`
  fragment BTabsBlockFragment on Block {
    _template
    settings {
      ...SettingsFragment
    }
    btab_list {
      __typename
      ...BTabItemFragment
    }
    tab_position
    show_bunnies_image
  }
`;
